<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Link } from '@inertiajs/vue3';
import { useCookieConsent, type ConsentState } from '@/composables/useCookieConsent';
import Switch from '@/components/ui/switch/Switch.vue';

const { t } = useI18n();
const { consentGiven, acceptAll, rejectAll, savePreferences } = useCookieConsent();

const showDetails = ref(false);
const preferences = ref<ConsentState>({ analytics: false, marketing: false });

function handleSave() {
    savePreferences({ ...preferences.value });
}
</script>

<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="translate-y-full opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-full opacity-0"
        >
            <div
                v-if="!consentGiven"
                role="dialog"
                :aria-label="t('consent.title')"
                aria-modal="false"
                class="fixed bottom-0 inset-x-0 z-[9999] border-t border-border bg-background/95 backdrop-blur-sm shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
            >
                <div class="mx-auto max-w-7xl px-4 py-3 md:px-6 lg:px-8">
                    <!-- Slim bar -->
                    <template v-if="!showDetails">
                        <div class="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                            <p class="text-[13px] text-muted-foreground leading-relaxed md:flex-1">
                                {{ t('consent.description') }}<br class="hidden md:block">{{ ' ' }}{{ t('consent.descriptionCreator') }}
                                <Link href="/cookies" class="underline underline-offset-4">{{ t('consent.learnMore') }}</Link>
                            </p>
                            <div class="grid grid-cols-[auto_1fr_1fr] items-center gap-2 md:flex md:shrink-0">
                                <button
                                    class="text-[12px] text-muted-foreground underline underline-offset-4 cursor-pointer bg-transparent border-none py-2 px-1 md:py-1.5"
                                    @click="rejectAll"
                                >{{ t('consent.rejectAll') }}</button>
                                <button
                                    class="rounded-md border border-border bg-background text-foreground px-3.5 py-2 text-[12px] font-medium cursor-pointer text-center md:py-1.5"
                                    @click="showDetails = true"
                                >{{ t('consent.manage') }}</button>
                                <button
                                    class="rounded-md bg-foreground text-background px-3.5 py-2 text-[12px] font-medium cursor-pointer text-center md:py-1.5"
                                    @click="acceptAll"
                                >{{ t('consent.acceptAll') }}</button>
                            </div>
                        </div>
                    </template>

                    <!-- Detail view -->
                    <template v-else>
                        <div class="space-y-3">
                            <div class="flex items-center justify-between gap-3">
                                <div class="min-w-0">
                                    <p class="text-[13px] font-medium">{{ t('consent.categories.essential.title') }}</p>
                                    <p class="text-[11px] text-muted-foreground">{{ t('consent.categories.essential.description') }}</p>
                                </div>
                                <Switch :model-value="true" disabled class="opacity-50 shrink-0" />
                            </div>
                            <div class="flex items-center justify-between gap-3">
                                <div class="min-w-0">
                                    <p class="text-[13px] font-medium">{{ t('consent.categories.analytics.title') }}</p>
                                    <p class="text-[11px] text-muted-foreground">{{ t('consent.categories.analytics.description') }}</p>
                                </div>
                                <Switch v-model="preferences.analytics" class="shrink-0" />
                            </div>
                            <div class="flex items-center justify-between gap-3">
                                <div class="min-w-0">
                                    <p class="text-[13px] font-medium">{{ t('consent.categories.marketing.title') }}</p>
                                    <p class="text-[11px] text-muted-foreground">{{ t('consent.categories.marketing.description') }}</p>
                                </div>
                                <Switch v-model="preferences.marketing" class="shrink-0" />
                            </div>
                        </div>
                        <div class="mt-3 grid grid-cols-2 gap-2 md:flex md:justify-end">
                            <button
                                class="rounded-md border border-border bg-background text-foreground px-3.5 py-2 text-[12px] font-medium cursor-pointer text-center md:py-1.5"
                                @click="acceptAll"
                            >{{ t('consent.acceptAll') }}</button>
                            <button
                                class="rounded-md bg-foreground text-background px-3.5 py-2 text-[12px] font-medium cursor-pointer text-center md:py-1.5"
                                @click="handleSave"
                            >{{ t('consent.savePreferences') }}</button>
                        </div>
                    </template>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>
